import './DraggableCustomParameters.scss';
import { useEffect, useState, useMemo } from 'react';
import DraggableDiv from "../index"
import CustomParameterSection from "./CustomParameterSection"
import CommonParameterSection from "./CommonParameterSection"
import { useDispatch, useSelector } from "react-redux";
import { getParametersOfSelectedElements } from 'store/actions/viewer';
import { registerOnSelectionChangeCustomParameters,
    unregisterOnSelectionChangeCustomParameters,
    setCPWindowPositionToLocalStorage,
    getCPWindowPositionFromLocalStorage,
    registerExportSelection
} from '../../../utils/builder/IScheduleLogic'
import { ResizableBox } from 'react-resizable';
import { useTranslation } from 'react-i18next';
import { getCommonPropertiesInOrder } from '../../../utils/builder/IScheduleLogic'
import { useDashboardState } from 'components/DashboardContext';

// sort object params according to the original order in BIMSearch class
function sortObjectByList(obj, keyOrder) {
  const sorted = {};

  keyOrder.forEach(key => {
    if (obj.hasOwnProperty(key)) {
      sorted[key] = obj[key];
    }
  });

  return sorted;
}

function getCustomParametersInCommon(parametersOfSelection) {
    if (!parametersOfSelection) return []

    const resultMap = {}

    for (const elemId of Object.keys(parametersOfSelection)) {
        const elemCustomParamsList = parametersOfSelection[elemId].customProperties

        for (const customParam of elemCustomParamsList) {
            const paramName = customParam.name
            const currentValue = customParam.selectedValue

            if (!resultMap[paramName]) {
                // Take a copy of the entire object, not just name and selectedValue
                resultMap[paramName] = { ...customParam }
                continue
            }

            const existingValue = resultMap[paramName].selectedValue

            // Only change selectedValue if it differs
            if (existingValue !== "Varies" && existingValue !== currentValue) {
                resultMap[paramName].selectedValue = "Varies"
            }
        }
    }
    
    // Return array of full parameter objects
    return Object.values(resultMap)
}

// parametersOfSelection - Object { 
//  elemId: { 
//      common: {
//          paramName: value 
//      },
//      custom: {
//         [CustomParameter]
//      }
//  }
function getCommonParametersInCommon(parametersOfSelection) {
    const result = {}
    const countMap = {}
    const totalElements = Object.keys(parametersOfSelection).length

    for (const elemId of Object.keys(parametersOfSelection)) {
        const elemCommonParamsObject = parametersOfSelection[elemId].common

        for (const commonParamName of Object.keys(elemCommonParamsObject)) {
            const currentValue = elemCommonParamsObject[commonParamName]

            if (!result[commonParamName]) {
                result[commonParamName] = currentValue
                countMap[commonParamName] = 1
                continue
            }

            countMap[commonParamName] += 1

            // Apply "Varies" logic
            if (result[commonParamName] !== "Varies" && result[commonParamName] !== currentValue) {
                result[commonParamName] = "Varies"
            }
        }
    }

    for (const paramName of Object.keys(result)) {
        if (countMap[paramName] !== totalElements) {
            delete result[paramName]
        }
    }

    return result
}


export default function DraggableCustomParameters({ onClose }) {
    
    const commonPropertiesInOrder = getCommonPropertiesInOrder()

    const [customParametersOpen, setCustomParametersOpen] = useState(true)
    const [commonParametersOpen, setCommonParametersOpen] = useState(true)

    const { adskViewerSelection, parametersOfSelection } = useSelector((state) => state.viewer);

    const { getLabelByIsTranslateModelEnabled } = useDashboardState()
    const { t } = useTranslation()

    const adskSelectionLength = adskViewerSelection?.dbIdArray?.length ?? 0;
    
    const dispatch = useDispatch()
    
    useEffect(() => {
        if (parametersOfSelection && Object.keys(parametersOfSelection).length) {
            dispatch(getParametersOfSelectedElements());
        }
        // register in order to use selection change event in bimsearchviewer
        registerOnSelectionChangeCustomParameters(() => {dispatch(getParametersOfSelectedElements())})

        return () => unregisterOnSelectionChangeCustomParameters()
    }, []);

    const custom = getCustomParametersInCommon(parametersOfSelection)

    let orderedCommonParameters = {}
    if (parametersOfSelection) {
        orderedCommonParameters = sortObjectByList(getCommonParametersInCommon(parametersOfSelection), commonPropertiesInOrder)
    }

    const savedPosition = useMemo(() => {return getCPWindowPositionFromLocalStorage()}, [])

    return (
        <DraggableDiv 
            style={{"top": savedPosition.top, "left": savedPosition.left}}>
            <ResizableBox 
            id="custom-parameters-resizableBox"
            width={savedPosition.width}
            height={savedPosition.height}
            handle={<div className="react-resizable-handle"></div>}
            minConstraints={[250, 100]}
            maxConstraints={[1000, 1000]}
            axis="both"
            resizeHandles={['se']}
            onResizeStop={(e, data) => {
                setCPWindowPositionToLocalStorage()
            }}>
            <div className="custom-parameters-menu">
                <div className="custom-parameters-header drag">
                    {t('bimsearch_parameters')} {`(${adskSelectionLength})`}
                    <button type="button" className="close-button" onClick={onClose} >
                        X
                    </button>
                </div>
                <div className="custom-parameters-articles-container">
                    <article className="custom-parameters-article">
                        <div className={`custom-parameters-subheader${customParametersOpen ? ' open' : ''}`}
                             onClick={() => {setCustomParametersOpen(prev => !prev)}}
                             >
                            {t('custom_parameters')}
                        </div>
                        {customParametersOpen && custom && custom.map((customParameterObject, idx) => {
                            return <CustomParameterSection
                            key={`${customParameterObject.name + idx}`}
                            customParameterObject={customParameterObject}
                            />
                        })
                    }
                    </article>
                    <article className="common-parameters-article">
                        <div className={`custom-parameters-subheader${commonParametersOpen ? ' open' : ''}`}
                        onClick={() => {setCommonParametersOpen(prev => !prev)}}
                        >
                            {t('common_parameters')}
                        </div>
                        {commonParametersOpen && Object.keys(orderedCommonParameters).length > 0 && Object.keys(orderedCommonParameters).map(key => {
                            return <CommonParameterSection
                            key={`${key + orderedCommonParameters[key]}`}
                            commonParameterObject={{
                                name: getLabelByIsTranslateModelEnabled(key) || key,
                                value: orderedCommonParameters[key]
                            }}
                            />
                        })
                    }
                    </article>
                </div>
            </div>
            </ResizableBox>
        </DraggableDiv>
    );
}